import { useState } from 'react'
import { Badge, type BadgeColor } from 'app/components/ui/badge'
import { Button } from 'app/components/ui/button'
import type { DemoPerson } from 'app/types/related-contacts'
import { recipientFor } from 'lib/relatedContacts'
import { DEMO_FAMILIES } from './demoFamilies'

type StepKind = `text` | `waiting` | `skipped` | `reply` | `confirmed` | `call`

type Step = { day: string; kind: StepKind; title: string; detail?: string }

const KIND_BADGES: Record<StepKind, { label: string; color: BadgeColor }> = {
  text: { label: `Text sent`, color: `blue` },
  waiting: { label: `Waiting`, color: `amber` },
  skipped: { label: `Skipped`, color: `red` },
  reply: { label: `Reply`, color: `gray` },
  confirmed: { label: `Confirmed`, color: `green` },
  call: { label: `AI call`, color: `purple` },
}

function buildSteps(): Step[] {
  const steps: Step[] = []
  const backlog: { day: string; person: DemoPerson; familyId: string }[] = []

  for (const family of DEMO_FAMILIES) {
    const due = family.members.filter((m) => m.hygieneDue)
    due.forEach((person, index) => {
      const recipient = recipientFor(person, family)
      const first = person.name.split(` `)[0]
      if (recipient.kind === `none`) {
        steps.push({
          day: `Monday`,
          kind: `skipped`,
          title: `${first}: no adult to contact`,
          detail: `${first} is under 18 and no parent or guardian is linked, so nothing is sent. The run shows why.`,
        })
        return
      }
      if (index > 0) {
        backlog.push({ day: `Tuesday`, person, familyId: family.id })
        steps.push({
          day: `Monday`,
          kind: `waiting`,
          title: `${first}: waiting for tomorrow`,
          detail: `The ${person.name.split(` `).at(-1)} family already got a workflow text today.`,
        })
        return
      }
      const to = recipient.person.name.split(` `)[0]
      steps.push({
        day: `Monday`,
        kind: `text`,
        title: recipient.kind === `self` ? `Text to ${first}` : `Text to ${to}, about ${first}`,
        detail:
          recipient.kind === `self`
            ? `"Hi ${first}, you're due for your cleaning."`
            : `"Hi ${to}, ${first} is due for a cleaning."`,
      })
    })
  }

  for (const { day, person, familyId } of backlog) {
    const family = DEMO_FAMILIES.find((f) => f.id === familyId)!
    const recipient = recipientFor(person, family)
    if (recipient.kind === `none`) continue
    const first = person.name.split(` `)[0]
    const to = recipient.person.name.split(` `)[0]
    steps.push({
      day,
      kind: `text`,
      title: `Text to ${to}, about ${first}`,
      detail: `"Hi ${to}, ${first} is due for a cleaning."`,
    })
  }

  steps.push(
    {
      day: `Thursday`,
      kind: `text`,
      title: `Reminder to Linda, about Jake`,
      detail: `"Reminder: Jake's visit is today at 3:00 PM. Reply C to confirm." Reminders are never held back.`,
    },
    {
      day: `Thursday`,
      kind: `text`,
      title: `Reminder to Linda, about Ava`,
      detail: `"Reminder: Ava's visit is today at 4:00 PM. Reply C to confirm."`,
    },
    {
      day: `Thursday`,
      kind: `reply`,
      title: `Linda replies "C"`,
      detail: `Two reminders went to this number today, so "C" confirms neither yet.`,
    },
    {
      day: `Thursday`,
      kind: `text`,
      title: `Trackable asks which one`,
      detail: `"Thanks Linda! Which are you confirming: Jake at 3:00 PM, Ava at 4:00 PM, or both? Reply JAKE, AVA or BOTH."`,
    },
    { day: `Thursday`, kind: `reply`, title: `Linda replies "BOTH"` },
    {
      day: `Thursday`,
      kind: `confirmed`,
      title: `Jake 3:00 PM and Ava 4:00 PM confirmed`,
      detail: `Only an unclear second answer would go to staff.`,
    },
    {
      day: `Friday`,
      kind: `call`,
      title: `AI follow-up call for Ethan dials Diane`,
      detail: `Ethan is 16, so the voice agent calls his mother, the guarantor. It never dials a minor's own number.`,
    },
  )
  return steps
}

const STEPS = buildSteps()

export function WorkflowSimulation() {
  const [shown, setShown] = useState(0)
  const visible = STEPS.slice(0, shown)

  return (
    <section className="rounded-xl border border-default bg-surface p-4" data-testid="related-contacts-simulation">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-body-md font-semibold text-strong">Run the hygiene recall workflow</h2>
          <p className="text-body-sm text-subtle">
            Steps through what each family receives. Simulated: nothing is sent.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="tertiary" size="sm" disabled={shown === 0} onClick={() => setShown(0)}>
            Reset
          </Button>
          <Button variant="tertiary" size="sm" disabled={shown === STEPS.length} onClick={() => setShown(STEPS.length)}>
            Show all
          </Button>
          <Button
            size="sm"
            disabled={shown === STEPS.length}
            onClick={() => setShown(shown + 1)}
            data-testid="simulation-next"
          >
            {shown === 0 ? `Run workflow` : `Next step`}
          </Button>
        </div>
      </div>
      {visible.length > 0 && (
        <ol className="mt-4 flex flex-col gap-2">
          {visible.map((step, index) => (
            <li key={index} className="flex gap-3 rounded-lg border border-default p-3">
              <span className="w-20 shrink-0 text-body-xs text-subtle">{step.day}</span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge label={KIND_BADGES[step.kind].label} color={KIND_BADGES[step.kind].color} />
                  <span className="text-body-sm font-medium text-strong">{step.title}</span>
                </div>
                {step.detail && <p className="mt-1 text-body-sm text-default">{step.detail}</p>}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}
